import { DataProvider } from '@refinedev/core';
import { AxiosInstance } from 'axios';
import { stringify } from 'query-string';
import { baseAxiosInstance, generateFilter, generateSort } from './utils';

import { AppConfig } from '@/config/appConfig';
import { formatJsonApiData } from '@/lib/index';
import { useAuthStore } from '@/store';

type MethodTypes = 'get' | 'delete' | 'head' | 'options';
type MethodTypesWithBody = 'post' | 'put' | 'patch';

const axiosInstance = baseAxiosInstance;

axiosInstance.interceptors.request.use((config: any) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers ||= {};
    if (
      config.url?.includes(AppConfig.aiServiceUrlOld) ||
      config.url?.includes(AppConfig.blazeUrl)
    ) {
      // AI service (old) and the Blaze/Hono service (APL-11716) authenticate
      // via Authorization: Bearer.
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // if token is in the header go service throws CORS error as token is not allowed in the Options request
      config.headers['token'] = `${token}`;
    }
  }
  // Blaze/Hono submissions routing (APL-11716): tag EVERY Blaze request with
  // X-App-Env so the queue consumer replays to api-{env}.assessprep.com. Set
  // centrally here (not per-call) so the backup write, the submission POST, and
  // any future Blaze call carry it uniformly; harmless on endpoints that ignore
  // it (e.g. /backup). Independent of the token branch above — it's a build-env
  // header, not auth.
  if (config.url?.includes(AppConfig.blazeUrl)) {
    config.headers ||= {};
    config.headers['X-App-Env'] = AppConfig.mode;
  }
  return config;
});

// Cache disabled due to issues with failed requests not being retried properly.
// Example: experiences_verify_join_code returns 404 for invalid codes, but subsequent calls to the same endpoint are cached and don't make new API requests.
// const cachedAxiosInstance = setupCache(axiosInstance);
const cachedAxiosInstance = axiosInstance;

// const cachedAxiosInstance = setupCache(axiosInstance, {
//   location: 'client',
//   // maxAge: 0,
//   // staleIfError: false,
//   // staleIfExpired: false,
//   // cacheKey: (config) => {
//   //   return config.url;
//   // }
// });

export { axiosInstance, cachedAxiosInstance };

const getSingularResourceKey = (resource: string) => {
  return resource.replace(/s$/, '');
};

const getQueryString = (data: any = {}) => {
  const queryParams = Object.keys(data)
    .map((key) => {
      const value = data[key];

      if (Array.isArray(value)) {
        // For arrays, append '[]' to the key and encode it
        const arrayKeyWithBrackets = `${key}[]`;
        // const encodedKeyWithBrackets = encodeURIComponent(arrayKeyWithBrackets);
        const encodedKeyWithBrackets = arrayKeyWithBrackets;

        // Map over the array values, creating key[]=value pairs
        return value
          .map((item) => `${encodedKeyWithBrackets}=${encodeURIComponent(item)}`)
          .join('&');
      } else {
        // For single values, just encode the key and value
        // const encodedKey = encodeURIComponent(key);
        const encodedKey = key;
        return `${encodedKey}=${encodeURIComponent(value)}`;
      }
    })
    .join('&'); // Join all key-value pairs with '&'
  const queryString = queryParams ? `${queryParams}` : '';
  return queryString;
};
export const dataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): DataProvider => ({
  createMany: async ({ resource, variables, meta }) => {
    const { headers, method, url, resourceKey } = meta ?? {};
    const finalUrl = `${apiUrl}/${url}` || `${apiUrl}/${resource}/bulk_create`;

    console.error('mr-react-simple-rest meta 16', meta);
    const requestMethod = (method as MethodTypesWithBody) ?? 'post';
    let finalVariables: any = variables;
    if (resourceKey) {
      finalVariables = {
        [resourceKey]: variables,
      };
    }
    const { data } = await httpClient[requestMethod](finalUrl, finalVariables, {
      headers,
    });

    return {
      data,
    };
  },

  updateMany: async ({ resource, ids, variables, meta }) => {
    const { headers, method, url } = meta ?? {};
    const finalUrl = `${apiUrl}/${url}` || `${apiUrl}/${resource}/bulk_update`;

    const requestMethod = (method as MethodTypesWithBody) ?? 'post';

    const { data } = await httpClient[requestMethod](finalUrl, variables, {
      headers,
    });

    return {
      data,
    };
  },

  deleteMany: async ({ resource, ids, meta }) => {
    const { headers, method, url } = meta ?? {};
    const finalUrl = `${apiUrl}/${url}` || `${apiUrl}/${resource}/bulk_delete`;

    const requestMethod = (method as MethodTypesWithBody) ?? 'post';

    const { data } = await httpClient[requestMethod](
      finalUrl,
      { ids },
      {
        headers,
      }
    );

    return {
      data,
    };
  },
  // ): Omit<
  //     Required<DataProvider>,
  //     "createMany" | "updateMany" | "deleteMany"
  // > => ({
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    if (meta?.overrideDataSource) {
      const overrideDataSource = meta?.overrideDataSource({ filters, pagination, sorters });

      if (overrideDataSource) {
        return {
          data: overrideDataSource,
          meta: {},
          total: overrideDataSource.length,
        };
      }
    }
    const url = `${apiUrl}/${resource}`;

    const { current = 1, pageSize = 10, mode = 'server' } = pagination ?? {};

    const { headers: headersFromMeta, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? 'get';

    const queryFilters = generateFilter(filters);
    console.log('queryFilters 311', filters);
    console.log('queryFilters 312', queryFilters);
    const query: {
      _start?: number;
      _end?: number;
      _sort?: string;
      _order?: string;
      page_size?: number;
      page?: number;
    } = {};

    if (mode === 'server') {
      // query._start = (current - 1) * pageSize;
      // query._end = current * pageSize;
      //
      query.page = current;
      query.page_size = pageSize;
    }

    const generatedSort = generateSort(sorters);
    if (generatedSort) {
      const { _sort, _order } = generatedSort;
      query._sort = _sort.join(',');
      query._order = _order.join(',');
    }
    console.log('queryFilters 313', queryFilters);
    const queryString = getQueryString(queryFilters);
    console.log('queryString 315', queryString);
    const { data, headers } = await httpClient[requestMethod](
      `${url}?${stringify(query)}&${queryString}`,
      {
        headers: headersFromMeta,
      }
    );

    // const { data, headers } = await httpClient[requestMethod](
    //   `${url}?${stringify(query)}&${stringify((queryFilters))}`,
    //   {
    //     headers: headersFromMeta,
    //   },
    // );
    let finalData = data?.[resource]?.data ?? data?.[resource] ?? data?.data ?? data;

    if (meta?.formatJsonApiData) {
      finalData = formatJsonApiData(finalData);
    }

    if (meta?.processData) {
      finalData = meta.processData(finalData);
    }

    let rawData;
    if (meta?.returnFullResponse) {
      rawData = data;
    }
    console.log('getList data ==>', finalData);

    // console.log("dataProvider 113", data);
    const finalMeta = data?.meta ?? {};
    // console.log("apdebug 113", finalData);
    // const finalMeta = data?.meta || {};
    const finalTotal = data?.total_count ?? 0;

    // const total = +headers["x-total-count"];

    return {
      data: finalData,
      rawData: rawData,
      meta: finalMeta,
      // headers,
      total: finalTotal,
    };
  },

  getMany: async ({ resource, ids, meta }) => {
    const { headers, method } = meta ?? {};
    console.log('orgSelectProps 10 getMany meta', meta);
    // const requestMethod = (method as MethodTypes) ?? "get";
    const requestMethod = (method as MethodTypesWithBody) ?? 'get';
    // console.log("orgSelectProps getMany", ids)
    if (typeof ids === 'string' || typeof ids === 'number') {
      ids = [ids];
    }
    console.log('orgProgrammeSelect 10 getMany', ids);
    const queryParams = Array.isArray(ids)
      ? ids.map((id) => `by_ids[]=${id}`).join('&')
      : `by_ids[]=${ids}`;
    const { data } = await httpClient[requestMethod](`${apiUrl}/${resource}?${queryParams}`, {
      headers,
    });
    console.log('orgProgrammeSelect 10 getMany meta', meta);
    console.log('orgProgrammeSelect 10 getMany data', data);
    // let finalData = data;
    console.log('orgProgrammeSelect 10 getMany data[resource]', data?.[resource]?.data);
    let finalData = data?.[resource]?.data ?? data?.[resource] ?? data?.data ?? data;
    // if (Array.isArray(finalData) && finalData.length === 0) {
    //   finalData = finalData[0];
    // }
    if (meta?.formatJsonApiData) {
      finalData = formatJsonApiData(finalData);
    }
    // finalData = data?.[resource]?.data?.attributes || data?.data || data;
    // if (meta?.returnNestedData) {
    // }
    console.log('getMany data ==>', finalData);

    return {
      data: finalData,
    };
  },

  create: async ({ resource, variables, meta }) => {
    const url = `${apiUrl}/${resource}`;
    // const queryString = getQueryString(meta?.query);
    // let url = baseUrl;
    // if (queryString) {
    //   url = `${baseUrl}?${queryString}`;
    // }
    console.log('mr-react-simple-rest meta 144 create only', meta);
    const { headers, method, query = {} } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? 'post';

    const singularResourceKey = resource.replace(/s$/, '');
    const finalVariables: any = {
      [singularResourceKey]: variables,
      ...query,
    };

    console.log('dataProvider create URL', url);
    const { data } = await httpClient[requestMethod](url, finalVariables, {
      headers,
    });

    let finalData = data;
    if (meta?.formatJsonApiData) {
      finalData = formatJsonApiData(finalData);
    }

    return {
      data: finalData,
    };
  },

  update: async ({ resource, id, variables, meta }) => {
    const baseUrl = `${apiUrl}/${resource}/${id}.json`;
    const queryString = getQueryString(meta?.query);
    let url = baseUrl;
    if (queryString) {
      url = `${baseUrl}?${queryString}`;
    }

    const { headers, method, query = {} } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? 'put';
    console.log('dataProvider variables', variables, meta);

    const singularResourceKey = resource.replace(/s$/, '');
    let finalVariables: any = variables;
    if (singularResourceKey !== 'org') {
      //! need to discuss this
      finalVariables = {
        [singularResourceKey]: variables,
        ...query,
      };
    }

    console.log('dataProvider finalVariables', finalVariables);
    console.log('dataProvider update URL', url);
    const { data } = await httpClient[requestMethod](url, variables, {
      headers,
    });
    let finalData = data;
    if (meta?.formatJsonApiData) {
      finalData = data?.[singularResourceKey] ?? data?.data ?? data;
      finalData = formatJsonApiData(finalData);
    }
    console.log('dataProvider update finalData', finalData, data);
    return {
      data: finalData,
    };
  },

  getOne: async ({ resource, id, meta }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? 'get';
    const queryString = getQueryString(meta?.query);
    const finalUrl = queryString ? `${url}?${queryString}` : url;
    const { data } = await httpClient[requestMethod](finalUrl, { headers });

    console.log('getOne data ==>', data);
    const singularResourceKey = getSingularResourceKey(resource);
    const finalData = data?.[singularResourceKey]?.data?.attributes ?? data?.data ?? data;

    // let finalData = data;
    // if (meta?.formatJsonApiData) {
    //   finalData = formatJsonApiData(finalData);
    // }

    return {
      data: finalData,
    };
  },

  deleteOne: async ({ resource, id, variables, meta }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? 'delete';

    const { data } = await httpClient[requestMethod](url, {
      data: variables,
      headers,
    });

    let finalData = data;
    if (meta?.formatJsonApiData) {
      finalData = formatJsonApiData(finalData);
    }

    return {
      data: finalData,
    };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({ url, method, filters, sorters, payload, query, headers }) => {
    console.log('CheckUseCustom', apiUrl);

    let requestUrl = `${apiUrl}/${url}`;
    let hasQueryParams = false;

    if (filters) {
      const queryFilters = generateFilter(filters);
      const queryString = getQueryString(queryFilters);
      requestUrl = `${requestUrl}${queryString}`;
      hasQueryParams = true;
    }

    if (sorters && sorters.length > 0) {
      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(','),
          _order: _order.join(','),
        };
        const separator = hasQueryParams ? '&' : '?';
        requestUrl = `${requestUrl}${separator}${new URLSearchParams(sortQuery).toString()}`;
        hasQueryParams = true;
      }
    }

    if (query) {
      const separator = hasQueryParams ? '&' : '?';
      requestUrl = `${requestUrl}${separator}${stringify(query)}`;
    }

    const requestMethod = method ?? 'get';
    let axiosResponse;

    switch (requestMethod) {
      case 'put':
      case 'post':
      case 'patch':
        axiosResponse = await httpClient[requestMethod](requestUrl, payload, {
          headers,
        });
        break;
      case 'delete':
        axiosResponse = await httpClient.delete(requestUrl, {
          data: payload,
          headers,
        });
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl, {
          headers,
        });
        break;
    }

    const { data } = axiosResponse;

    return {
      data,
    };
  },
});

export const defaultDataProvider = dataProvider(AppConfig.apiUrl, axiosInstance);
export const aiServiceDataProvider = dataProvider(AppConfig.aiServiceUrl, axiosInstance);
// Blaze/Hono service (APL-11716). Reuses the shared axiosInstance — since
// AppConfig.blazeUrl is absolute, the factory's `${apiUrl}/${url}` yields the
// full Blaze URL (the instance has no baseURL to double-prefix). The request
// interceptor above sends Authorization: Bearer for blazeUrl requests (Blaze's
// documented primary). Reached via useCustom / useCustomMutation with
// dataProviderName: 'blaze'.
export const blazeDataProvider = dataProvider(AppConfig.blazeUrl, axiosInstance);
